import { Button, Card, Center, Chip, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { GoldRain } from '../components/GoldRain.js';
import { MatchRain } from '../components/MatchRain.js';
import { RejectModal } from '../components/RejectModal.js';
import { SearchProfileFilter } from '../components/SearchProfileFilter.js';
import { ShareModal } from '../components/ShareModal.js';
import { SwipeCard } from '../components/SwipeCard.js';
import { SwipeHint } from '../components/SwipeHint.js';
import {
  getObject,
  getSearchProfiles,
  getSwipeQueue,
  rejectObject,
  shortlistObject,
  unswipeObject,
  type ObjectView,
  type RejectCategory,
} from '../lib/api.js';

const FRESH_MAX_DAYS = 3; // „≤ 3 Tage"-Toggle an
const HARD_MAX_DAYS = 21; // Toggle aus → trotzdem nie älter als 21 Tage einblenden

export function Swipe(): JSX.Element {
  const [searchProfileId, setSearchProfileId] = useState<number | undefined>(undefined);
  // Zwei ankreuzbare Filter, beide standardmäßig AN:
  const [overThreshold, setOverThreshold] = useState(true); // nur ≥ Phase-3-Schwelle
  const [fresh, setFresh] = useState(true); // nur ≤ 3 Tage alte Inserate
  // IDs die in dieser Session schon weggeswipet wurden. Verhindert Race-Conditions
  // zwischen optimistischem Weiterwechseln und dem async Queue-Refetch.
  const [swipedIds, setSwipedIds] = useState<Set<number>>(new Set());
  const [rejectModal, setRejectModal] = useState<{ id: number } | null>(null);
  const [shareModal, setShareModal] = useState<{
    id: number;
    text: string;
    title: string;
    url?: string;
  } | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [lastSwipe, setLastSwipe] = useState<{ id: number; action: 'reject' | 'shortlist' | 'match' } | null>(null);
  // Match-Feier: Zeitstempel-Trigger (imperativ aus der Server-Antwort matched:true).
  const [matchTrigger, setMatchTrigger] = useState<number>();
  const qc = useQueryClient();

  const { data: queueData, isLoading: queueLoading } = useQuery({
    queryKey: ['swipe-queue', searchProfileId, overThreshold, fresh],
    queryFn: () =>
      getSwipeQueue(
        searchProfileId,
        // AN → undefined: Backend nutzt die per-Profil Phase-3-Schwelle (p3Threshold).
        // AUS → 0 (alle Scores).
        overThreshold ? undefined : 0,
        fresh ? FRESH_MAX_DAYS : HARD_MAX_DAYS,
      ),
    staleTime: 30_000,
  });

  // Prozent-Label des Schwellen-Chips = Phase-3-Schwelle (gewähltes Profil, sonst
  // die höchste der aktiven Profile als konservativer Anhaltspunkt).
  const { data: profilesData } = useQuery({
    queryKey: ['search-profiles'],
    queryFn: getSearchProfiles,
    staleTime: 60_000,
  });
  const activeProfiles = (profilesData?.profiles ?? []).filter((p) => p.active);
  const selectedProfile = activeProfiles.find((p) => p.id === searchProfileId);
  const p3 =
    selectedProfile?.config.p3Threshold ??
    (activeProfiles.length > 0
      ? Math.max(...activeProfiles.map((p) => p.config.p3Threshold ?? 0.65))
      : 0.65);
  const thresholdPct = Math.round(p3 * 100);

  // Queue ohne die lokal-geswipten IDs. Das nächste Karten-Subjekt ist immer queue[0].
  const queue = useMemo(
    () => (queueData?.queue ?? []).filter((id) => !swipedIds.has(id)),
    [queueData, swipedIds],
  );
  const currentId = queue[0];
  const totalFromServer = queueData?.queue?.length ?? 0;
  const swipedCount = swipedIds.size;

  const { data: currentObj, isLoading: objLoading } = useQuery<ObjectView>({
    queryKey: ['object', currentId],
    queryFn: () => getObject(currentId!),
    enabled: currentId !== undefined,
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, category, note }: { id: number; category: RejectCategory; note: string }) =>
      rejectObject(id, category, note || undefined),
    onSuccess: (_, vars) => {
      setLastSwipe({ id: vars.id, action: 'reject' });
      setSwipedIds((prev) => {
        const next = new Set(prev);
        next.add(vars.id);
        return next;
      });
      qc.invalidateQueries({ queryKey: ['objects'] });
      qc.invalidateQueries({ queryKey: ['swipe-queue'] });
      setRejectModal(null);
      setRejectNote('');
    },
    onError: (err) =>
      notifications.show({ message: `Fehler: ${String(err)}`, color: 'red' }),
  });

  const shortlistMut = useMutation({
    mutationFn: (id: number) => shortlistObject(id),
    onSuccess: (res, id) => {
      // Match! (beide Logins Hot) → Feier-Animation; sonst der Teilen-Dialog wie bisher.
      if (res.matched) {
        setMatchTrigger(Date.now());
      } else if (currentObj) {
        setShareModal({
          id,
          text: res.whatsappText,
          title: currentObj.listing?.title ?? 'Inserat',
          url: currentObj.listing?.url ?? undefined,
        });
      }
      setLastSwipe({ id, action: res.matched ? 'match' : 'shortlist' });
      setSwipedIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      qc.invalidateQueries({ queryKey: ['objects'] });
      qc.invalidateQueries({ queryKey: ['swipe-queue'] });
      if (res.matched) {
        qc.invalidateQueries({ queryKey: ['nav-badge-match'] });
        qc.invalidateQueries({ queryKey: ['matches'] });
      }
    },
    onError: (err) =>
      notifications.show({ message: `Fehler: ${String(err)}`, color: 'red' }),
  });

  const undoMut = useMutation({
    mutationFn: (id: number) => unswipeObject(id),
    onSuccess: (_, id) => {
      setLastSwipe(null);
      setSwipedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      qc.invalidateQueries({ queryKey: ['objects'] });
      qc.invalidateQueries({ queryKey: ['swipe-queue'] });
    },
  });

  const handleSwipeLeft = (): void => {
    if (!currentId) return;
    // Nur bei Top-Treffern (Score > 0,90) nach dem Grund fragen — sonst sofort ablehnen.
    const score = currentObj?.score?.effectiveTotal ?? 0;
    if (score > 0.9) {
      setRejectModal({ id: currentId });
    } else {
      rejectMut.mutate({ id: currentId, category: 'sonstiges', note: '' });
    }
  };

  const handleSwipeRight = (): void => {
    if (!currentId) return;
    shortlistMut.mutate(currentId);
  };

  const dismissShareModal = (): void => {
    setShareModal(null);
  };

  const remaining = queue.length;
  const done = remaining === 0;

  // Top-Treffer (≥ 95%): Betongold-Feier-Animation feuern, sobald so eine Karte erscheint.
  const goldTrigger =
    currentObj && (currentObj.score?.effectiveTotal ?? 0) >= 0.95 ? currentObj.id : undefined;

  return (
    // Füllt die sichtbare Höhe, damit die Fußleiste (margin-top:auto) unten sitzt statt
    // mittig zu schweben, wenn die Karte kurz ist.
    <Stack gap="sm" style={{ minHeight: 'calc(100dvh - 150px)' }}>
      {/* Eingeblendete Kurzanleitung (Erstbesuch + 30 s-Erinnerung mobil) statt Dauertext. */}
      <SwipeHint activityKey={currentId} />

      {/* Betongold-Feier bei Top-Treffern (≥ 95%). */}
      <GoldRain trigger={goldTrigger} />
      {/* Match-Feier: beide wollen dieselbe Immobilie. */}
      <MatchRain trigger={matchTrigger} />

      {/* Kopf nur auf Desktop/Tablet — am Handy sagt die Bottom-Nav schon „Swipe" und
          der Platz ist knapp. */}
      <Stack gap={4} visibleFrom="sm">
        <Title order={2}>Swipe</Title>
      </Stack>

      {/* Zeile 1: Profil-Auswahl (Alle | Wohnung | Haus | Grundstück) */}
      <SearchProfileFilter
        value={searchProfileId}
        onChange={(id) => {
          setSearchProfileId(id);
          setSwipedIds(new Set());
        }}
      />

      {/* Zeile 2: die zwei Hackerl-Filter LINKS, offene Menge RECHTS (gleiche Zeile). */}
      <Group justify="space-between" wrap="nowrap" gap="xs" align="center">
        <Group gap="xs" wrap="wrap" align="center">
          <Chip
            checked={fresh}
            onChange={(c) => {
              setFresh(c);
              setSwipedIds(new Set());
            }}
            size="sm"
            variant="outline"
          >
            ≤ {FRESH_MAX_DAYS} Tage
          </Chip>
          <Chip
            checked={overThreshold}
            onChange={(c) => {
              setOverThreshold(c);
              setSwipedIds(new Set());
            }}
            size="sm"
            variant="outline"
          >
            ≥ {thresholdPct}%
          </Chip>
        </Group>
        <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
          {totalFromServer === 0
            ? '0'
            : `${swipedCount + 1} / ${totalFromServer}`}
        </Text>
      </Group>

      {(queueLoading || (currentId && objLoading)) && (
        <Center py="xl">
          <Loader />
        </Center>
      )}

      {!queueLoading && done && (
        <Card padding="xl">
          <Stack align="center" gap="md">
            <Text size="xl" fw={700}>
              🎉 Du bist durch
            </Text>
            <Text c="dimmed" ta="center">
              Keine offenen Treffer mehr in diesem Filter. Neue kommen automatisch nach jedem
              Mail-Poll (alle 15 Min) oder durch Status-Reset.
            </Text>
          </Stack>
        </Card>
      )}

      {!done && currentObj && (
        <SwipeCard
          key={currentObj.id}
          object={currentObj}
          onSwipeLeft={handleSwipeLeft}
          onSwipeRight={handleSwipeRight}
        />
      )}

      {/* Fixierte Fußleiste: Undo (falls vorhanden) + Aktions-Buttons — immer erreichbar
          ohne Scrollen. `.swipe-actions` klärt am Handy die Bottom-Nav (siehe index.css). */}
      {(( !done && currentId) || lastSwipe) && (
        <div className="swipe-actions">
          <Stack gap="xs">
            {lastSwipe && (
              <Card
                withBorder
                padding="xs"
                style={{ background: 'rgba(15,23,42,0.92)', color: '#fff' }}
              >
                <Group justify="space-between" align="center" wrap="nowrap">
                  <Text size="sm" style={{ color: '#fff' }}>
                    {lastSwipe.action === 'reject'
                      ? '✗ Pass'
                      : lastSwipe.action === 'match'
                        ? '🎯 Match — ihr wollt beide dieses Objekt!'
                        : '👍 Hot — wartet auf deinen Partner'}
                  </Text>
                  <Button
                    size="xs"
                    variant="white"
                    color="dark"
                    onClick={() => undoMut.mutate(lastSwipe.id)}
                    disabled={undoMut.isPending}
                  >
                    ↶ Rückgängig
                  </Button>
                </Group>
              </Card>
            )}
            {!done && currentId && (
              <Group justify="space-around">
                <Button
                  color="red"
                  variant="light"
                  size="lg"
                  radius="xl"
                  onClick={handleSwipeLeft}
                  disabled={!currentObj || rejectMut.isPending || shortlistMut.isPending}
                  style={{ flex: 1, maxWidth: 160, height: 52, fontSize: '1rem' }}
                  leftSection="✗"
                >
                  Pass
                </Button>
                <Button
                  color="green"
                  variant="filled"
                  size="lg"
                  radius="xl"
                  onClick={handleSwipeRight}
                  disabled={!currentObj || rejectMut.isPending || shortlistMut.isPending}
                  style={{ flex: 1, maxWidth: 160, height: 52, fontSize: '1rem' }}
                  leftSection="★"
                >
                  Hot
                </Button>
              </Group>
            )}
          </Stack>
        </div>
      )}

      <RejectModal
        opened={rejectModal !== null}
        onClose={() => {
          setRejectModal(null);
          setRejectNote('');
        }}
        note={rejectNote}
        onNoteChange={setRejectNote}
        onPick={(category) => {
          if (rejectModal) rejectMut.mutate({ id: rejectModal.id, category, note: rejectNote });
        }}
        pending={rejectMut.isPending}
      />

      <ShareModal
        opened={shareModal !== null}
        onClose={dismissShareModal}
        text={shareModal?.text ?? ''}
        title={shareModal?.title ?? ''}
        listingUrl={shareModal?.url}
      />
    </Stack>
  );
}
